<script setup lang="ts">
  import { computed } from 'vue';
  import PosUiChip from '@/components/pos-v2/ui/PosUiChip.vue';
  import { toggleStation } from '@/domain/catalog/printing-form';
  import { useRestaurantStore } from '@/stores/restaurant.store';

  /**
   * The store's print stations as toggle chips (plan §5.12), with the amber
   * warning when none is chosen. Shared by the product and category editors.
   */
  const props = defineProps<{
    value: number[];
    /** Accessible name of the group. */
    label: string;
    /** Shown when no station is chosen. */
    emptyWarning: string;
    disabled?: boolean;
  }>();

  const emit = defineEmits<{ (e: 'input', value: number[]): void }>();

  const restaurantStore = useRestaurantStore();
  const stations = computed(() =>
    [...restaurantStore.printStations].sort((a, b) => a.index - b.index)
  );
</script>

<template>
  <div class="v2-catalog-station-chips">
    <div class="chips-list" role="group" :aria-label="label">
      <PosUiChip
        v-for="station in stations"
        :key="station.stationId"
        class="station-chip"
        :active="value.includes(station.stationId)"
        :disabled="disabled"
        @click="emit('input', toggleStation(value, station.stationId))"
        >{{ station.name }}</PosUiChip
      >
    </div>
    <p v-if="!value.length" class="chips-warning" role="status">
      <i class="mdi mdi-alert-outline warning-icon" aria-hidden="true" />
      {{ emptyWarning }}
    </p>
  </div>
</template>

<style scoped lang="scss">
  .v2-catalog-station-chips {
    .chips-list {
      // Margins, not flex gap: old POS hardware (Chrome < 84).
      @apply -tw-mb-2 -tw-mr-2 tw-flex tw-flex-wrap;
    }

    .station-chip {
      @apply tw-mb-2 tw-mr-2;
    }

    .chips-warning {
      @apply tw-m-0 tw-mt-3 tw-flex tw-items-start tw-rounded-lg tw-bg-amber-50 tw-px-3 tw-py-2 tw-text-sm tw-text-amber-800;

      .warning-icon {
        @apply tw-mr-1.5 tw-text-base tw-leading-5;
      }
    }
  }
</style>
